import React, { useCallback } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import NotificationThreadListItem from './NotificationThreadListItem';
import NotificationThreadListHeader from './NotificationThreadListHeader';
import { useNotificationFilters, useFilteredNotifications, useScrollHeight } from './hooks';
import { parseNotificationChannels, stripHtmlTags } from './utils';
import { NotificationThreadListProps } from './types';
import '../NotificationDashboard/Notifications1.css';

/**
 * NotificationThreadList Component
 * 
 * Displays a list of notification threads with filtering, search, and infinite scroll capabilities.
 * Refactored to use custom hooks for better separation of concerns and improved maintainability.
 */
const NotificationThreadList: React.FC<NotificationThreadListProps> = (props) => {
    const {
        notifications,
        selectedContactGroup,
        selectedChannels,
        onComposeChannels,
        setActiveNotificationThread,
        resetChannelsHandler,
        selectedChannelFilter,
        applyChannelFilterHandler,
        resetChannelFilterHandler,
        deactivateNotificationHandler,
        loadMoreOnScroll,
        currentPage,
        hasMore,
        clearRecipientsSelection,
        applySearchText,
        isSearching
    } = props;

    // Use custom hooks for state management
    const { 
        searchFilter, 
        isDeactivateNotificationFilter 
    } = useNotificationFilters();

    // Filter notifications based on selected filters
    const filteredNotifications = useFilteredNotifications(
        notifications,
        selectedChannelFilter,
        isDeactivateNotificationFilter
    );

    // Calculate scroll container height
    const reducedHeight = useScrollHeight(selectedChannelFilter, searchFilter);

    /**
     * Handler for activating a notification thread
     */
    const activateNotificationThreadHandler = useCallback((
        id: string,
        notificationThreadStatus: string,
        channels: string[],
        title: string
    ) => {
        console.log('Activating Notification Thread:', id);
        setActiveNotificationThread(id, notificationThreadStatus, channels, title);
    }, [setActiveNotificationThread]);

    /**
     * Handler for loading more data on scroll
     */
    const fetchMoreData = useCallback(() => {
        loadMoreOnScroll(currentPage + 1);
    }, [loadMoreOnScroll, currentPage]);

    return (
        <>
            <div className={selectedChannelFilter.length > 0 ? 'filterHeightss' : ''}>
                <div className={searchFilter ? 'sarchHeightss' : ''}>
                    <NotificationThreadListHeader
                        selectedChannels={selectedChannels}
                        onComposeChannels={onComposeChannels}
                        resetChannelsHandler={resetChannelsHandler}
                        selectedChannelFilter={selectedChannelFilter}
                        applyChannelFilterHandler={applyChannelFilterHandler}
                        resetChannelFilterHandler={resetChannelFilterHandler}
                        deactivateNotificationHandler={deactivateNotificationHandler}
                        clearRecipientsSelection={clearRecipientsSelection}
                        applySearchText={applySearchText}
                        isSearching={isSearching}
                    />

                    <div 
                        className="messageNoti" 
                        id="threadsContainer" 
                        style={{
                            height: `calc(100% - ${reducedHeight}px)`,
                            overflowY: 'scroll',
                            position: 'relative'
                        }}
                    >
                        <InfiniteScroll
                            dataLength={filteredNotifications.length}
                            next={fetchMoreData}
                            hasMore={hasMore}
                            loader={false}
                            scrollableTarget="threadsContainer"
                            endMessage={<span className="noMoreData">No more data</span>}
                        >
                            {filteredNotifications.map((notificationThread) => {
                                const { channelsString, channelsArray } = parseNotificationChannels(notificationThread);
                                
                                // Update the channels on the notification thread
                                notificationThread.channels = channelsString;

                                return (
                                    <NotificationThreadListItem
                                        onClick={() => activateNotificationThreadHandler(
                                            notificationThread.usersGroupId,
                                            notificationThread.notificationThreadStatus,
                                            channelsArray,
                                            notificationThread.title
                                        )}
                                        contactGroup={notificationThread.usersGroupId}
                                        key={notificationThread.id}
                                        messageId={notificationThread.id}
                                        messageAuthorId={notificationThread.authorId}
                                        authorImageUrl={notificationThread.authorImageUrl}
                                        messageTitle={notificationThread.title}
                                        messageBody={stripHtmlTags(notificationThread.messageBody)}
                                        authorName={notificationThread.authorName}
                                        lastModified={notificationThread.lastModified}
                                        messageType="Message"
                                        selectedContactGroup={selectedContactGroup}
                                        channels={notificationThread.channels}
                                        notificationThreadStatus={notificationThread.notificationThreadStatus}
                                        onSelectDeactivateNotification={deactivateNotificationHandler}
                                        previewedRecipientData={notificationThread.previewedRecipientData}
                                        messageStatus={notificationThread.last_notification_status}
                                    />
                                );
                            })}
                        </InfiniteScroll>
                    </div>
                </div>
            </div>
        </>
    );
};

export default NotificationThreadList;
